/**
 * useOfflineStatus - Hook para detectar estado offline y cache
 * 
 * FUT-04: Cache tiles offline con Service Worker
 * 
 * @returns {Object} Estado de conexión y cache
 */

import { useState, useEffect, useCallback } from 'react'

interface OfflineStatus {
  /** Si el navegador está online */
  isOnline: boolean
  /** Si el Service Worker está activo */
  swActive: boolean
  /** Si hay actualizaciones pendientes del SW */
  swUpdateAvailable: boolean
  /** Forzar actualización del SW */
  updateServiceWorker: () => void
}

export function useOfflineStatus(): OfflineStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [swActive, setSwActive] = useState(false)
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false)
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    // Detectar cambios de conexión
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Verificar estado del Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setSwActive(true)
        setRegistration(reg)

        // Detectar actualizaciones
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setSwUpdateAvailable(true)
              }
            })
          }
        })
      }).catch(() => {
        setSwActive(false)
      })
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const updateServiceWorker = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      window.location.reload()
    }
  }, [registration])

  return {
    isOnline,
    swActive,
    swUpdateAvailable,
    updateServiceWorker
  }
}

/**
 * Información sobre el cache de tiles
 */
interface TileCacheInfo {
  name: string
  count: number
  sizeEstimate: string
}

/**
 * Hook para obtener estadísticas del cache
 */
export function useCacheStats() {
  const [cacheStats, setCacheStats] = useState<TileCacheInfo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function getCacheStats() {
      if (!('caches' in window)) {
        setLoading(false)
        return
      }

      try {
        const cacheNames = await caches.keys()
        const tileCaches = cacheNames.filter(name => 
          name.includes('tiles') || name.includes('dera') || name.includes('api')
        )

        const stats: TileCacheInfo[] = []

        for (const cacheName of tileCaches) {
          const cache = await caches.open(cacheName)
          const keys = await cache.keys()
          
          // Estimación de tamaño (aproximado)
          let totalSize = 0
          for (const request of keys.slice(0, 10)) {
            const response = await cache.match(request)
            if (response) {
              const blob = await response.clone().blob()
              totalSize += blob.size
            }
          }
          
          const avgSize = keys.length > 0 ? totalSize / Math.min(keys.length, 10) : 0
          const estimatedTotal = avgSize * keys.length

          stats.push({
            name: cacheName,
            count: keys.length,
            sizeEstimate: formatBytes(estimatedTotal)
          })
        }

        setCacheStats(stats)
      } catch (error) {
        console.error('Error obteniendo stats de cache:', error)
      } finally {
        setLoading(false)
      }
    }

    getCacheStats()
  }, [])

  return { cacheStats, loading }
}

/**
 * Limpiar cache de tiles
 */
export async function clearTileCache(cacheName?: string): Promise<boolean> {
  if (!('caches' in window)) return false

  try {
    if (cacheName) {
      return await caches.delete(cacheName)
    }

    // Limpiar todos los caches de tiles
    const cacheNames = await caches.keys()
    const tileCaches = cacheNames.filter(name => name.includes('tiles'))
    
    await Promise.all(tileCaches.map(name => caches.delete(name)))
    return true
  } catch (error) {
    console.error('Error limpiando cache:', error)
    return false
  }
}

// Utilidad para formatear bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export default useOfflineStatus
